import React, { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  BarChart2, 
  TrendingUp, 
  DollarSign, 
  Users, 
  Calendar,
  Car,
  MapPin,
  RefreshCw,
  Download,
  ArrowUpRight,
  ArrowDownRight,
  PieChart as PieIcon
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  Area,
  AreaChart
} from 'recharts';

const API_BASE = window.location.origin || '';

const COLORS = ['#f97316', '#22c55e', '#3b82f6', '#ef4444', '#8b5cf6', '#06b6d4'];
const STATUS_COLORS = {
  confirmed: '#22c55e',
  pending: '#f59e0b', 
  completed: '#3b82f6',
  cancelled: '#ef4444'
};

const AdminReportsPage = () => {
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [topRoutes, setTopRoutes] = useState([]);
  const [vehicleStats, setVehicleStats] = useState([]);
  const [dateRange, setDateRange] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statsRes, revenueRes, routesRes, vehiclesRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/reports/summary?range=${dateRange}`, { headers }),
        fetch(`${API_BASE}/api/admin/reports/revenue?range=${dateRange}`, { headers }),
        fetch(`${API_BASE}/api/admin/reports/top-routes?range=${dateRange}`, { headers }),
        fetch(`${API_BASE}/api/admin/reports/vehicles?range=${dateRange}`, { headers })
      ]);
      
      const [statsData, revenueDataRes, routesData, vehiclesData] = await Promise.all([
        statsRes.json(),
        revenueRes.json(),
        routesRes.json(),
        vehiclesRes.json()
      ]);
      
      if (statsData.success) setStats(statsData.stats);
      if (revenueDataRes.success) setRevenueData(revenueDataRes.data || []);
      if (routesData.success) setTopRoutes(routesData.routes || []);
      if (vehiclesData.success) setVehicleStats(vehiclesData.vehicles || []);
      
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Rapor yüklenemedi');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchReports();
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/reports/export?range=${dateRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `goturkeytransfer-rapor-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Rapor indirildi');
      } else {
        toast.error('Rapor indirilemedi');
      }
    } catch (error) {
      toast.error('İndirme başarısız');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    const num = parseFloat(value) || 0;
    return `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`;
  };

  // Prepare pie chart data for booking status
  const statusPieData = stats ? [
    { name: 'Onaylı', value: stats.confirmedBookings || 0, color: STATUS_COLORS.confirmed },
    { name: 'Beklemede', value: stats.pendingBookings || 0, color: STATUS_COLORS.pending },
    { name: 'Tamamlandı', value: stats.completedBookings || 0, color: STATUS_COLORS.completed },
    { name: 'İptal', value: stats.cancelledBookings || 0, color: STATUS_COLORS.cancelled },
  ].filter(item => item.value > 0) : [];

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 shadow-lg">
          <p className="text-gray-300 text-sm mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.dataKey === 'revenue' ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const StatCard = ({ title, value, change, icon: Icon, prefix = '', suffix = '' }) => {
    const isPositive = parseFloat(change) >= 0;
    return (
      <Card className="bg-zinc-900/80 border-zinc-800 hover:border-orange-500/30 transition-colors">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-400">{title}</p>
              <p className="text-2xl font-bold text-white mt-1">
                {prefix}{typeof value === 'number' ? value.toLocaleString('tr-TR') : value}{suffix}
              </p>
              {change !== undefined && (
                <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                  {isPositive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                  <span>{formatPercent(change)} öncekine göre</span>
                </div>
              )}
            </div>
            <div className="p-3 bg-orange-500/10 rounded-lg">
              <Icon className="h-6 w-6 text-orange-500" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-background admin-theme">
        <AdminSidebar />
        <main className="flex-1 p-6 lg:p-8 lg:ml-0">
          <div className="flex items-center justify-center min-h-[400px]">
            <RefreshCw className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background admin-theme">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8 lg:ml-0">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="reports-title">Raporlar & Analitik</h1>
              <p className="text-gray-400 text-sm mt-1">İş performansı ve içgörüler</p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Date Range Selector */}
              <div className="flex bg-zinc-900 rounded-lg p-1 border border-zinc-800">
                {[
                  { key: 'week', label: 'Hafta' },
                  { key: 'month', label: 'Ay' },
                  { key: 'quarter', label: 'Çeyrek' },
                  { key: 'year', label: 'Yıl' }
                ].map((range) => (
                  <button
                    key={range.key}
                    onClick={() => setDateRange(range.key)}
                    data-testid={`range-${range.key}`}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      dateRange === range.key 
                        ? 'bg-orange-500 text-white' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
              
              <Button 
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                disabled={isRefreshing}
                className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Yenile
              </Button>
              
              <Button 
                onClick={handleExport}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="export-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                İndir
              </Button>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Toplam Gelir" 
              value={formatCurrency(stats?.totalRevenue)} 
              change={stats?.revenueChange}
              icon={DollarSign}
            />
            <StatCard 
              title="Toplam Rezervasyon" 
              value={stats?.totalBookings || 0} 
              change={stats?.bookingsChange}
              icon={Calendar}
            />
            <StatCard 
              title="Yeni Müşteri" 
              value={stats?.newCustomers || 0} 
              change={stats?.customersChange}
              icon={Users}
            />
            <StatCard 
              title="Ort. Rezervasyon Tutarı" 
              value={formatCurrency(stats?.avgBookingValue)} 
              change={stats?.avgValueChange}
              icon={TrendingUp}
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend Chart */}
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardHeader className="border-b border-zinc-800">
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart2 className="h-5 w-5 text-orange-500" />
                  Gelir Trendi
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {revenueData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={revenueData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis 
                        dataKey="label" 
                        stroke="#9ca3af" 
                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                        axisLine={{ stroke: '#374151' }}
                      />
                      <YAxis 
                        stroke="#9ca3af" 
                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                        axisLine={{ stroke: '#374151' }}
                        tickFormatter={(value) => `€${value}`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#f97316" 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#colorRevenue)" 
                        name="Gelir"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-500">
                    Gelir verisi bulunamadı
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Booking Status Pie Chart */}
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardHeader className="border-b border-zinc-800">
                <CardTitle className="text-white flex items-center gap-2">
                  <PieIcon className="h-5 w-5 text-orange-500" />
                  Rezervasyon Durumu
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {statusPieData.length > 0 ? (
                  <div className="flex items-center">
                    <ResponsiveContainer width="50%" height={240}>
                      <PieChart>
                        <Pie
                          data={statusPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          fill="#8884d8"
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {statusPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#27272a', border: '1px solid #3f3f46', borderRadius: '8px' }}
                          itemStyle={{ color: '#fff' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex-1 space-y-3">
                      {statusPieData.map((status, index) => {
                        const total = stats?.totalBookings || 1;
                        const percent = ((status.value / total) * 100).toFixed(1);
                        return (
                          <div key={index} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.color }} />
                              <span className="text-gray-300 text-sm">{status.name}</span>
                            </div>
                            <span className="text-white font-medium">{status.value} ({percent}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-500">
                    Rezervasyon verisi bulunamadı
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Vehicle Stats Bar Chart */}
          <Card className="bg-zinc-900/80 border-zinc-800">
            <CardHeader className="border-b border-zinc-800">
              <CardTitle className="text-white flex items-center gap-2">
                <Car className="h-5 w-5 text-orange-500" />
                Araç Performansı
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {vehicleStats.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={vehicleStats} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="type" 
                      stroke="#9ca3af"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      axisLine={{ stroke: '#374151' }}
                    />
                    <YAxis 
                      yAxisId="left"
                      stroke="#9ca3af"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      axisLine={{ stroke: '#374151' }}
                    />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right"
                      stroke="#9ca3af"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      axisLine={{ stroke: '#374151' }}
                      tickFormatter={(value) => `€${value}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ color: '#9ca3af' }}
                      formatter={(value) => <span className="text-gray-300">{value}</span>}
                    />
                    <Bar 
                      yAxisId="left" 
                      dataKey="bookings" 
                      fill="#3b82f6" 
                      name="Rezervasyon" 
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar 
                      yAxisId="right" 
                      dataKey="revenue" 
                      fill="#f97316" 
                      name="Gelir (€)" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  Araç verisi bulunamadı
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bottom Row - Top Routes & Rates */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Routes */}
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardHeader className="border-b border-zinc-800">
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-orange-500" />
                  En Popüler Rotalar
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {topRoutes.length > 0 ? (
                  <div className="space-y-3">
                    {topRoutes.slice(0, 5).map((route, index) => (
                      <div key={index} className="flex items-center justify-between py-3 border-b border-zinc-800 last:border-0">
                        <div className="flex items-center gap-3">
                          <span 
                            className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                            style={{ backgroundColor: COLORS[index % COLORS.length] + '20', color: COLORS[index % COLORS.length] }}
                          >
                            {index + 1}
                          </span>
                          <div>
                            <p className="text-white text-sm font-medium">{route.from} → {route.to}</p>
                            <p className="text-gray-500 text-xs">{route.bookings} rezervasyon</p>
                          </div>
                        </div>
                        <span className="text-orange-500 font-semibold">{formatCurrency(route.revenue)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-gray-500">
                    Rota verisi bulunamadı
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Performance Rates */}
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardHeader className="border-b border-zinc-800">
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-orange-500" />
                  Performans Oranları
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 gap-6">
                  {/* Conversion Rate */}
                  <div className="text-center p-4 bg-zinc-800/50 rounded-xl">
                    <p className="text-4xl font-bold text-orange-500">{stats?.conversionRate || 0}%</p>
                    <p className="text-gray-400 text-sm mt-2">Teklif → Rezervasyon Dönüşümü</p>
                    <div className="mt-3 h-2 bg-zinc-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all"
                        style={{ width: `${Math.min(stats?.conversionRate || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Repeat Customer Rate */}
                  <div className="text-center p-4 bg-zinc-800/50 rounded-xl">
                    <p className="text-4xl font-bold text-green-500">{stats?.repeatCustomerRate || 0}%</p>
                    <p className="text-gray-400 text-sm mt-2">Tekrar Eden Müşteri Oranı</p>
                    <div className="mt-3 h-2 bg-zinc-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all"
                        style={{ width: `${Math.min(stats?.repeatCustomerRate || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Cancellation Rate */}
                  <div className="text-center p-4 bg-zinc-800/50 rounded-xl">
                    <p className="text-4xl font-bold text-red-500">{stats?.cancellationRate || 0}%</p>
                    <p className="text-gray-400 text-sm mt-2">İptal Oranı</p>
                    <div className="mt-3 h-2 bg-zinc-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full transition-all"
                        style={{ width: `${Math.min(stats?.cancellationRate || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminReportsPage;
